/**
 * Ecommerce DataLayer Utility for GA4
 *
 * Provides consent-aware dataLayer pushes for ecommerce events.
 * Uses the same Cookiebot consent pattern as public/js/datalayer.js
 */

import type { Packsize, DosageForm } from '../types';
import type { Product as ShopwareProduct, LineItem } from '../shopware/shopwareApi';
import { getEffectiveCalculatedPrice } from '../shopware/shopwareUtils';

// Log prefix for console messages (consistent with datalayer.js)
const DATALAYER_LOG_PREFIX = '[ORT-DATALAYER-ECOM] ';

// Currency is always EUR for the German shop
const CURRENCY = 'EUR';

// Brand is always Orthomol (single-brand shop)
const BRAND = 'Orthomol';

/**
 * GA4 Ecommerce Item structure
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item
 */
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  item_variant: string;
  quantity: number;
  price: number;
  currency: string;
}

/**
 * GA4 Ecommerce Event structure
 */
interface EcommerceEvent {
  event: string;
  ecommerce: {
    currency: string;
    value: number;
    items: GA4Item[];
  };
}

/**
 * Global type declarations for Cookiebot and window extensions
 */
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    CookieConsent?: {
      consent?: {
        marketing?: boolean;
      };
    };
  }
}

// Buffer for events when consent is not yet given
const MAX_BUFFERED_EVENTS = 100;
let eventBuffer: EcommerceEvent[] = [];
let isConsentListenerAttached = false;

/**
 * Check if Cookiebot marketing consent is given
 */
const hasMarketingConsent = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.CookieConsent !== 'undefined' &&
    window.CookieConsent?.consent?.marketing === true
  );
};

/**
 * Push event to dataLayer (internal)
 */
const pushToDataLayer = (event: EcommerceEvent): void => {
  if (typeof window === 'undefined') return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Fancy colored console output for debugging
  const eventColor = getEventColor(event.event);
  console.debug(
    `%c 📊 DataLayer Push %c ${event.event} %c`,
    'background: #6366f1; color: white; padding: 4px 8px; border-radius: 4px 0 0 4px; font-weight: bold;',
    `background: ${eventColor}; color: white; padding: 4px 8px; border-radius: 0 4px 4px 0; font-weight: bold;`,
    ''
  );
  console.debug(
    '%c ecommerce %c',
    'background: #374151; color: #9ca3af; padding: 2px 6px; border-radius: 4px; font-size: 11px;',
    '',
    event.ecommerce
  );

  // Clear previous ecommerce data to avoid conflicts (GA4 best practice)
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(event);
};

/**
 * Get color for event type
 */
const getEventColor = (eventName: string): string => {
  const colors: Record<string, string> = {
    view_item: '#10b981', // green
    add_to_cart: '#f59e0b', // amber
    remove_from_cart: '#ef4444', // red
    view_cart: '#3b82f6', // blue
    begin_checkout: '#8b5cf6', // purple
    purchase: '#22c55e', // bright green
    reserve_pharmacy: '#06b6d4', // cyan
  };
  return colors[eventName] || '#6b7280'; // gray fallback
};

/**
 * Flush buffered events after consent is given
 */
const flushBuffer = (): void => {
  if (eventBuffer.length === 0) return;

  console.info(DATALAYER_LOG_PREFIX + 'Flushing buffered ecommerce events after consent', eventBuffer.length, 'events');

  eventBuffer.forEach((event) => {
    pushToDataLayer(event);
  });

  eventBuffer = [];
};

/**
 * Attach consent listener (once)
 */
const attachConsentListener = (): void => {
  if (isConsentListenerAttached || typeof window === 'undefined') return;

  isConsentListenerAttached = true;

  window.addEventListener('CookiebotOnAccept', () => {
    if (hasMarketingConsent()) {
      console.info(DATALAYER_LOG_PREFIX + 'Marketing consent detected, flushing buffer');
      flushBuffer();
    }
  });
};

/**
 * Push ecommerce event with consent handling
 */
const pushEcommerceEvent = (event: EcommerceEvent): void => {
  const hasConsent = hasMarketingConsent();
  console.debug(DATALAYER_LOG_PREFIX + 'pushEcommerceEvent - hasMarketingConsent:', hasConsent);

  if (hasConsent) {
    pushToDataLayer(event);
  } else {
    if (eventBuffer.length < MAX_BUFFERED_EVENTS) {
      eventBuffer.push(event);
    } else {
      console.warn(DATALAYER_LOG_PREFIX + 'Event buffer full, dropping event');
    }
    attachConsentListener();
  }
};

/**
 * Look up the product category name for a given O-Number from the embedded JSON map
 */
let _categoryMapCache: Record<string, { name: string; key: string }> | null = null;
let _categoryMapLoaded = false;

function getProductCategory(oNumber: string): string {
  if (!_categoryMapLoaded) {
    _categoryMapLoaded = true;
    const el = document.getElementById('product-category-map-json');
    if (!el) {
      console.warn(DATALAYER_LOG_PREFIX + 'product-category-map-json element not found — item_category will be empty');
      return '';
    }
    try {
      _categoryMapCache = JSON.parse(el.textContent || '') as Record<string, { name: string; key: string }>;
    } catch (e) {
      console.error(DATALAYER_LOG_PREFIX + 'Failed to parse product-category-map-json', e);
      return '';
    }
  }
  return _categoryMapCache?.[oNumber]?.name ?? '';
}

// Structural minimum so both the PDP's full Packsize/DosageForm and the add-to-cart islands' slim
// AddToCartPacksize/AddToCartDosageForm (page-product-variants-json) can be passed
type GA4ItemPacksize = Pick<Packsize, 'oNumber' | 'size'> & Partial<Pick<Packsize, 'fullProductName'>>;
type GA4ItemDosageForm = Pick<DosageForm, 'name' | 'productName'>;

/**
 * Build GA4 item from PDP data (or from an add-to-cart island's slim variant data)
 */
export const buildGA4Item = (
  packsize: GA4ItemPacksize,
  dosageForm: GA4ItemDosageForm,
  shopwareProduct: ShopwareProduct | null,
  quantity: number = 1
): GA4Item | null => {
  console.debug(DATALAYER_LOG_PREFIX + 'buildGA4Item called with:', { packsize, dosageForm, shopwareProduct, quantity });

  // item_id: o-number (productNumber)
  const itemId = packsize.oNumber;
  if (!itemId) {
    console.warn(DATALAYER_LOG_PREFIX + 'Cannot build GA4 item: missing oNumber');
    return null;
  }

  // item_name: main product name (e.g., "Orthomol Immun") from DosageFormJSON
  const itemName = dosageForm.productName || packsize.fullProductName || dosageForm.name || 'Unknown Product';

  const itemCategory = getProductCategory(itemId);

  // item_variant: dosageForm.name + packsize.size
  const itemVariant = `${dosageForm.name} ${packsize.size}`.trim();

  // price: effective (rule-based calculatedPrices[0] else calculatedPrice), matching the PDP display
  const price = getEffectiveCalculatedPrice(shopwareProduct)?.unitPrice ?? 0;

  return {
    item_id: itemId,
    item_name: itemName,
    item_brand: BRAND,
    item_category: itemCategory,
    item_variant: itemVariant,
    quantity,
    price,
    currency: CURRENCY,
  };
};

/**
 * Build GA4 item from cart LineItem
 * Used for tracking quantity changes in the cart
 */
export const buildGA4ItemFromLineItem = (lineItem: LineItem, quantity: number): GA4Item | null => {
  // item_id: productNumber from payload
  const itemId = lineItem.payload?.productNumber as string | undefined;
  if (!itemId) {
    console.warn(DATALAYER_LOG_PREFIX + 'Cannot build GA4 item from LineItem: missing productNumber');
    return null;
  }

  // Extract product info from payload options
  const options = (lineItem.payload?.options as Array<{ group: string; option: string }>) || [];
  const hauptprodukt = options.find((opt) => opt.group === 'Hauptprodukt')?.option || lineItem.label || 'Produkt';
  const darreichungsform = options.find((opt) => opt.group === 'Darreichungsform')?.option || '';
  const packsize = options.find((opt) => opt.group === 'Packungsgröße')?.option || '';

  // item_name: Hauptprodukt (main product name)
  const itemName = hauptprodukt;

  // item_variant: Darreichungsform + Packungsgröße
  const itemVariant = darreichungsform && packsize ? `${darreichungsform} ${packsize}` : darreichungsform || '';

  const itemCategory = getProductCategory(itemId);

  // price: from lineItem
  const price = lineItem.price?.unitPrice ?? 0;

  return {
    item_id: itemId,
    item_name: itemName,
    item_brand: BRAND,
    item_category: itemCategory,
    item_variant: itemVariant,
    quantity,
    price,
    currency: CURRENCY,
  };
};

/**
 * Push view_item event
 *
 * Trigger: PDP page load, variant change
 */
export const pushViewItem = (item: GA4Item): void => {
  console.debug(DATALAYER_LOG_PREFIX + 'pushViewItem called with:', item);

  const value = item.price * item.quantity;

  const event: EcommerceEvent = {
    event: 'view_item',
    ecommerce: {
      currency: CURRENCY,
      value,
      items: [item],
    },
  };

  pushEcommerceEvent(event);
};

/**
 * Push add_to_cart event
 *
 * Trigger: Add to cart button click on PDP
 */
export const pushAddToCart = (item: GA4Item): void => {
  console.debug(DATALAYER_LOG_PREFIX + 'pushAddToCart called with:', item);

  const value = item.price * item.quantity;

  const event: EcommerceEvent = {
    event: 'add_to_cart',
    ecommerce: {
      currency: CURRENCY,
      value,
      items: [item],
    },
  };

  pushEcommerceEvent(event);
};

/**
 * Push remove_from_cart event
 *
 * Trigger: Remove item from cart, decrease quantity in cart
 */
export const pushRemoveFromCart = (item: GA4Item): void => {
  console.debug(DATALAYER_LOG_PREFIX + 'pushRemoveFromCart called with:', item);

  const value = item.price * item.quantity;

  const event: EcommerceEvent = {
    event: 'remove_from_cart',
    ecommerce: {
      currency: CURRENCY,
      value,
      items: [item],
    },
  };

  pushEcommerceEvent(event);
};

/**
 * Push reserve_pharmacy event
 *
 * Trigger: Click on "In Apotheke reservieren" button
 */
export const pushReservePharmacy = (item: GA4Item): void => {
  const value = item.price * item.quantity;

  const event: EcommerceEvent = {
    event: 'reserve_pharmacy',
    ecommerce: {
      currency: CURRENCY,
      value,
      items: [item],
    },
  };

  pushEcommerceEvent(event);
};

/**
 * Push view_cart event
 *
 * Trigger: Page load of cart page (/shop/cart)
 * Note: This should NOT be triggered in the cart drawer, only on the full cart page
 */
export const pushViewCart = (items: GA4Item[], totalValue: number): void => {
  console.debug(DATALAYER_LOG_PREFIX + 'pushViewCart called with:', { items, totalValue });

  const event: EcommerceEvent = {
    event: 'view_cart',
    ecommerce: {
      currency: CURRENCY,
      value: totalValue,
      items,
    },
  };

  pushEcommerceEvent(event);
};
